import React from 'react';
import { push } from 'redux-first-history';
import { useAppDispatch } from '../../../../../store/hooks';
import type {
  IdentityVerificationInfo as BackendIdentityVerificationInfo,
  VerifyLandlordIdentityExtendedWebApiRequestDto,
} from '@zg-rentals/ts-rent-payment-client';
import {
  DEFAULT_ERROR,
  EMERGENCY_VERIFICATION_NEEDED,
  EMERGENCY_VERIFICATION_SUBMITTED,
} from '@rental-manager/constants/NotificationConstants';
import { getLandlordStatus } from '@rental-manager/actions/requests';
import { showModalNotification, showPageNotification } from '@common/actions/NotificationActions';

import pageFlows from './pageFlows';
import TaxTypeSelectorPage from './modules/TaxTypeSelectorPage';
import BusinessTypeSelectorPage from './modules/BusinessTypeSelectorPage';
import TaxInfoInputPage from './modules/TaxInfoInputPage';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { useVerifyLandlordIdentity } from '@rental-manager/hooks/useVerifyLandlordIdentity';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { getSignInPage } from '@common/paymentsUtils/api';
import { getPaymentErrorInfo } from '../../../../utils/getPaymentErrorInfo';

export type IdentityVerificationInfo = Omit<BackendIdentityVerificationInfo, 'dobDay' | 'dobMonth' | 'dobYear'> & {
  dobDay: string | number;
  dobMonth: string | number;
  dobYear: string | number;
  selectedTaxVerificationType: string;
};

interface ReduxAction {
  type: string;
  payload?: unknown;
  error?: unknown;
}

type OnSubmitData = { identityVerificationInfo: IdentityVerificationInfo | Record<string, unknown> };

type Props = {
  onSubmit: (data: OnSubmitData) => Promise<ReduxAction>;
  onClose: () => void;
  onBack: () => void;
  identityVerificationInfo: any;
  phoneNumber: string;
  verifiedPhoneNumber: string;
  emergencyMode: boolean;
  landlordBirthDayMax: string;
  landlordBirthDayMin: string;
  getIdVerificationInfo: () => any;
  getEmergencyVerificationPayload: (
    identityVerificationInfo: IdentityVerificationInfo | Record<string, unknown>,
  ) => VerifyLandlordIdentityExtendedWebApiRequestDto;
  saveTaxVerificationInfo: (idVerificationInfo: Partial<IdentityVerificationInfo>, selectedOption: string) => void;
  baseUrl: string;
  propertyId: string;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
};

const TaxVerificationPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const defaultPage = props.identityVerificationInfo?.editMode
    ? pageFlows.TaxInfoInputPage
    : pageFlows.TaxTypeSelectorPage;
  const defaultSelectedOption = props.identityVerificationInfo?.editMode
    ? props.identityVerificationInfo?.selectedTaxVerificationType
    : '';
  const [currentPage, setCurrentPage] = React.useState<string>(defaultPage);
  const [selectedOption, setSelectedOption] = React.useState<string>(defaultSelectedOption);
  const dispatch = useAppDispatch();

  const handleEmergencyVerificationSuccess = async () => {
    let authExpired = false;
    await dispatch(getLandlordStatus({ propertyId: props.propertyId })).catch((err) => {
      const { httpStatus } = getPaymentErrorInfo(err);
      if (httpStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location = getSignInPage();
        authExpired = true;
      }
    });
    if (authExpired) return;
    dispatch(push(props.baseUrl));
    dispatch(showPageNotification(EMERGENCY_VERIFICATION_SUBMITTED));
  };

  const handleEmergencyVerificationError = (_error: unknown) => {
    dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Verification', verb: 'submitted' }));
  };

  // Only use React Query hook in emergency mode
  const { mutate: verifyLandlordIdentity } = useVerifyLandlordIdentity({
    postVerificationFlow: handleEmergencyVerificationSuccess,
    onError: handleEmergencyVerificationError,
    context: 'TaxVerificationPage - verifyLandlordIdentity (emergency)',
  });

  React.useEffect(() => {
    if (props.emergencyMode) {
      dispatch(showModalNotification(EMERGENCY_VERIFICATION_NEEDED));
    }

    // Only try fetching the prefill data from the server once.
    if (!props.identityVerificationInfo?.requestStatus) {
      Promise.resolve().then(() => props.getIdVerificationInfo());
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const { phoneNumber, verifiedPhoneNumber, emergencyMode, landlordBirthDayMax, landlordBirthDayMin } = props;

  const callClickstreamEvent = (action?: string) => {
    const trackingData = { currentProperty: props.currentProperty, user: props.user };

    if (action === 'BACK') {
      if (currentPage === pageFlows.TaxInfoInputPage) {
        return IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
          ...trackingData,
          triggerObject: 'id_verify',
        });
      } else {
        return IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
          ...trackingData,
          triggerObject: 'tax_information',
        });
      }
    }

    if (action === 'CLOSE') {
      if (currentPage === pageFlows.TaxInfoInputPage) {
        return IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
          ...trackingData,
          triggerObject: 'id_verify',
        });
      } else {
        return IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
          ...trackingData,
          triggerObject: 'tax_information',
        });
      }
    }

    switch (selectedOption) {
      case 'single_member_llc':
        return IreneOnboardingCSEvents.emitContinueFromTaxVerificationPageAsSingleLLCEvent(trackingData);
      case 'multi_member_llc':
        return IreneOnboardingCSEvents.emitContinueFromTaxVerificationPageAsMultiLLCEvent(trackingData);
      case 'company':
        return IreneOnboardingCSEvents.emitContinueForBusinessEvent(trackingData);
      case 'individual':
        return IreneOnboardingCSEvents.emitContinueForIndividualEvent(trackingData);
      default:
        return null;
    }
  };

  const onSubmit = () => {
    callClickstreamEvent();

    if (selectedOption === 'company') {
      setSelectedOption('');
      setCurrentPage(pageFlows.BusinessTypeSelectorPage);
    } else {
      setCurrentPage(pageFlows.TaxInfoInputPage);
    }
  };

  const onBack = (idVerificationInfo: Partial<IdentityVerificationInfo> = {}): void => {
    callClickstreamEvent('BACK');
    if (currentPage === pageFlows.TaxInfoInputPage) {
      props.saveTaxVerificationInfo(idVerificationInfo, selectedOption);
    }
    if (currentPage === pageFlows.TaxTypeSelectorPage) {
      props.onBack();
    } else if (
      currentPage === pageFlows.BusinessTypeSelectorPage ||
      (selectedOption === 'individual' && currentPage === pageFlows.TaxInfoInputPage)
    ) {
      setSelectedOption(selectedOption === 'individual' ? 'individual' : 'company');
      setCurrentPage(pageFlows.TaxTypeSelectorPage);
    } else if (
      (selectedOption === 'single_member_llc' || selectedOption === 'multi_member_llc') &&
      currentPage === pageFlows.TaxInfoInputPage
    ) {
      setCurrentPage(pageFlows.BusinessTypeSelectorPage);
    }
  };

  const onClose = (): void => {
    callClickstreamEvent('CLOSE');
    props.onClose();
  };

  const inputPageProps = {
    ...props.identityVerificationInfo,
    phoneNumber,
    verifiedPhoneNumber,
    emergencyMode,
    landlordBirthDayMax,
    landlordBirthDayMin,
  };

  return (
    <>
      {currentPage === pageFlows.TaxTypeSelectorPage && (
        <TaxTypeSelectorPage
          onSubmit={onSubmit}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          onClose={onClose}
          onBack={onBack}
          emergencyMode={emergencyMode}
        />
      )}
      {currentPage === pageFlows.BusinessTypeSelectorPage && (
        <BusinessTypeSelectorPage
          onSubmit={onSubmit}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          onClose={onClose}
          onBack={onBack}
          emergencyMode={emergencyMode}
        />
      )}
      {currentPage === pageFlows.TaxInfoInputPage && (
        <TaxInfoInputPage
          {...inputPageProps}
          getIdVerificationInfo={props.getIdVerificationInfo}
          selectedOption={selectedOption}
          onClose={onClose}
          onBack={onBack}
          onCancel={props.onBack}
          onSubmit={(data) => {
            if (props.emergencyMode) {
              const payload = props.getEmergencyVerificationPayload(data.identityVerificationInfo);
              return new Promise<{ error?: boolean } | void>((resolve) => {
                verifyLandlordIdentity(
                  { postData: payload },
                  {
                    onSettled: () => resolve(),
                    onError: () => resolve({ error: true }),
                  },
                );
              });
            }
            return props.onSubmit(data);
          }}
          emergencyMode={emergencyMode}
          currentProperty={props.currentProperty}
          user={props.user}
        />
      )}
    </>
  );
};

export default TaxVerificationPage;
